import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import * as Actions from '../../../actions/index';

import { Button } from 'primereact/components/button/Button';
import { Column } from 'primereact/components/column/Column';
import { ContextMenu } from 'primereact/components/contextmenu/ContextMenu';
import { DataTable } from 'primereact/components/datatable/DataTable';
import { Dropdown } from 'primereact/components/dropdown/Dropdown';
import { InputText } from 'primereact/components/inputtext/InputText';
import { Toast } from 'primereact/components/toast/Toast';
import { Panel } from 'primereact/components/panel/Panel';
import { Toolbar } from 'primereact/components/toolbar/Toolbar';
import { Calendar } from 'primereact/calendar';
import { ProgressBar } from 'primereact/progressbar';

import {
    getAllPatients,
    getPatientById,
    getPatientsByAltRef,
    getPatientsByDOB,
    getPatientsByEmail,
    getPatientsByFirstname,
    getPatientsByPostcode,
    getPatientsByProvider,
    getPatientsBySurname,
    getPatientsByTelnumber
} from "../../../actions/findPatients";

import {
    DOC_IMAGE,
    HM_AddBillingGroup,
    HM_EditBillingGroup,
    TT_KnowledgeBase,
    HM_notImplemented,
    HM_PATIENT_IMAGE,
    HM_PatientBillingGroup,
    HM_RESEND_PORTAL_REFERENCE,
    HM_SEND_EMAIL,
    HM_SEND_SMS,
    HM_SEND_SMS_NO_MOBILE_NUMBER,
    SU_STATUS_ACTIVE,
    TB_SAVE_AND_EXIT
} from "../../../Constants";

import {
    ICON_CAMERA,
    ICON_EMAIL,
    ICON_GOTO,
    ICON_GROUP,
    ICON_HELP,
    ICON_PLUS,
    ICON_SMS,
    ICON_USER_MINUS,
    ICON_USER_PLUS,
} from "../../../icons";

import {
    showAddPatientPage,
    showPatientDetailsPage,
    showPaymentPlanHistoryPage,
    showTreatmentPlanHistoryPage
} from "../PatientDetails/Utils";

import {
    HELP_FIND_PATIENT,
    setState,
    SM_PATIENT_SEARCH,
    SM_TAB_PATIENT_CONSENT,
    SM_TAB_PATIENT_DETAILS,
    SM_TAB_PATIENT_MED_CON,
    SM_TAB_PATIENT_QUESTIONNAIRE,
    stateRequest
} from "../../../actions/stateManagement";

import {
    ICON_PATIENT_FEMALE,
    ICON_PATIENT_MALE,
    TB_ADD_PATIENT,
    TB_PATIENT_DETAILS,
    TB_PATIENT_PPLANS,
    TB_PATIENT_TPLANS,
} from "../PatientDetails/Constants";

import { ShowMessageDialog, ShowQuestionDialog } from "../Diary/components/EventComponent";
import _ from "lodash";
import { checkBox, CONST_FEMALE, CONST_MALE } from "../../PatientDynamicItems/OnChangeUtils";
import { dateTemplate, rowBGColour } from "../../PatientDynamicItems/Utils";
import { TAB_EXIT, TAB_EXIT_SAVE, TAB_PARENT_CHANGE } from "../Housekeeping/Constants";

import {
    deleteBillingGroup,
    getResource as getPatResource,
    RES_FIND_PATIENTS,
    RES_PATIENT_BILLING_GROUP,
    RES_PATIENT_DETAILS_SHORT,
    RES_PATIENT_EMAIL,
    RES_PATIENT_SMS,
    RES_RESEND_PATIENT_PORTAL,
    resendPortalReference,
    saveBillingGroupFromFind,
    sendEmail,
    sendSMS
} from "../../../actions/personal";

import * as DefaultData from "../DefaultData";
import { ac } from "../../../index";
import { addToLists, RES_TABLET_LISTS } from "../../../actions/tablet";
import { BillingGroup } from "../../PatientDynamicItems/BillingGroup";
import { getResource } from "../../../actions/nhsManagement";
import AddBillingGroup from "./AddBillingGroup";
import { getAllUsers } from "../../../actions/users";
import { ShowPatientImage } from "../../PatientDynamicItems/ShowPatientImage";
import { nhsTemplate } from "./Utils";
import TabletConsents from "../Diary/dialogs/TabletConsents";
import SendSMS from "../Utilities/dialogs/SendSMS";
import { tb_boilerPlateRight } from "../../Utils";
import { getResource as getResourceHSK, RES_TEMPLATE_NOTES } from "../../../actions/housekeeping";
import SendEmail from "../Utilities/dialogs/SendEmail";
import { converter } from "../fixedItemUtils";

// Custom hook for state persistence
const useStatePersistence = (stateId, initialState) => {
    const dispatch = useDispatch();
    const [saveFlag, setSaveFlag] = useState(false);
    
    useEffect(() => {
        if (saveFlag) {
            dispatch(setState(stateId, initialState));
            setSaveFlag(false);
        }
    }, [saveFlag, dispatch, stateId, initialState]);
    
    const triggerSave = useCallback(() => {
        setSaveFlag(true);
    }, []);
    
    return triggerSave;
};

// Custom hook to replace BaseComponent functionality
const useBaseComponentFunctionality = () => {
    const [exitState] = useState(TAB_PARENT_CHANGE);
    const tabletGrowl = useRef(null);
    const searchInput = useRef(null);
    
    return {
        exitState,
        tabletGrowl,
        searchInput
    };
};

export const FindPatients = () => {
    const dispatch = useDispatch();
    
    // Redux state selectors
    const message = useSelector(state => state.stateManagement.message);
    const results = useSelector(state => state.patients.results);
    const findPatientBillingGroupLoaded = useSelector(state => state.patients.findPatientBillingGroupLoaded);
    const findPatientBillingGroup = useSelector(state => state.patients.findPatientBillingGroup);
    const patientDataShortLoaded = useSelector(state => state.patients.short_loaded);
    const patientDataShort = useSelector(state => state.patients.short);
    const loginIdentity = useSelector(state => state.login.user);
    const templateNotesLoaded = useSelector(state => state.housekeeping.templateNotesLoaded);
    const templateNotes = useSelector(state => state.housekeeping.templateNotes);
    const usersLoaded = useSelector(state => state.users.searchComplete);
    const usersShort = useSelector(state => state.users.results);
    const capabilities = useSelector(state => state.login.capabilities);
    const currentState = useSelector(state => state.stateManagement[SM_PATIENT_SEARCH.id]);
    
    // Local state initialization
    const initialState = useMemo(() => {
        if (currentState) {
            return currentState.data;
        }
        return {
            canSave: { status: false, activeIndex: 0, source: RES_FIND_PATIENTS.CLEAR },
            searchCriteria: Actions.SEARCH_SURNAME,
            searchString: '',
            dob: null,
            provider: { id: null },
            placeHolder: 'Surname',
            dataTableSelectValue: null,
            checkboxes: {
                includeInactives: false,
            },
            results: [],
            first: 0,
            rows: 5,
            memberSelection: null,
            firstMember: 0,
            memberRows: 5,
            findPatientBillingGroup: null,
            removeFromFindList: true,
            reloadBillingGroup: false,
            [HM_AddBillingGroup.id]: false,
            [HM_RESEND_PORTAL_REFERENCE.id]: false,
        };
    }, [currentState]);
    
    // Local state hooks
    const [state, setState] = useState(initialState);
    
    // Base component functionality
    const { exitState, tabletGrowl, searchInput } = useBaseComponentFunctionality();
    
    // State persistence
    const triggerSave = useStatePersistence(SM_PATIENT_SEARCH.id, state);
    
    // Action dispatchers
    const actionDispatchers = useMemo(() => ({
        getAllUsersShort: () => dispatch(getAllUsers()),
        getAllPatients: (includeInactives) => dispatch(getAllPatients(includeInactives)),
        getPatientById: (id) => dispatch(getPatientById(id)),
        getPatientsBySurname: (urlPattern) => dispatch(getPatientsBySurname(urlPattern)),
        getPatientsByFirstname: (urlPattern) => dispatch(getPatientsByFirstname(urlPattern)),
        getPatientsByAltRef: (urlPattern) => dispatch(getPatientsByAltRef(urlPattern)),
        getPatientsByEmail: (urlPattern) => dispatch(getPatientsByEmail(urlPattern)),
        getPatientsByPostcode: (urlPattern) => dispatch(getPatientsByPostcode(urlPattern)),
        getPatientsByTelnumber: (urlPattern) => dispatch(getPatientsByTelnumber(urlPattern)),
        getPatientsByDOB: (urlPattern) => dispatch(getPatientsByDOB(urlPattern)),
        getPatientsByProvider: (urlPattern) => dispatch(getPatientsByProvider(urlPattern)),
        getBillingGroup: (patientId, groupId) => dispatch(getPatResource(patientId, groupId, RES_PATIENT_BILLING_GROUP.GET)),
        sendSMS: (details) => dispatch(sendSMS(details)),
        sendEmail: (details) => dispatch(sendEmail(details)),
        getTemplateNotes: () => dispatch(getResourceHSK(RES_TEMPLATE_NOTES.GET)),
        setState: (id, data) => dispatch(setState(id, data)),
        stateRequest: (data) => dispatch(stateRequest(data)),
    }), [dispatch]);
    
    // Component mount effect (replaces componentDidMount)
    useEffect(() => {
        if (!Boolean(currentState)) {
            actionDispatchers.getTemplateNotes();
            actionDispatchers.getAllUsersShort();
        } else {
            if (currentState.reloadBillingGroup) {
                // Handle reload billing group logic
            }
        }
    }, [currentState, actionDispatchers]);
    
    // Component update effect (replaces componentDidUpdate)
    useEffect(() => {
        if (!message) return;
        
        switch (message.type) {
            case Actions.RECEIVE_PATIENT_SEARCH:
                setState(prevState => ({
                    ...prevState,
                    results: results,
                    first: 0
                }));
                triggerSave();
                break;
                
            case RES_PATIENT_BILLING_GROUP.ADD.action:
                triggerSave();
                
                const addList = _.map(state.results, (patient) => {
                    if (patient.id === state.dataTableSelectValue.id) {
                        const _patient = { ...patient };
                        _patient.principlePayee = true;
                        _patient.principleGroup = findPatientBillingGroup.id;
                        _patient.principleGroupDescription = findPatientBillingGroup.description;
                        return _patient;
                    } else {
                        return patient;
                    }
                });
                
                setState(prevState => ({
                    ...prevState,
                    findPatientBillingGroupLoaded: findPatientBillingGroupLoaded,
                    findPatientBillingGroup: findPatientBillingGroup,
                    results: addList,
                }));
                break;
                
            case RES_PATIENT_BILLING_GROUP.UPDATE_IN_FIND.action:
                triggerSave();
                
                const updateList = _.map(state.results, (patient) => {
                    if (patient.id === state.dataTableSelectValue.id) {
                        const _patient = { ...patient };
                        _patient.billee = true;
                        _patient.billingGroup = findPatientBillingGroup.id;
                        _patient.billingGroupDescription = findPatientBillingGroup.description;
                        return _patient;
                    } else {
                        return patient;
                    }
                });
                
                setState(prevState => ({
                    ...prevState,
                    findPatientBillingGroupLoaded: findPatientBillingGroupLoaded,
                    findPatientBillingGroup: findPatientBillingGroup,
                    results: updateList,
                }));
                break;
                
            case RES_PATIENT_BILLING_GROUP.REMOVE.action:
                triggerSave();
                // Handle remove action logic
                break;
                
            case RES_PATIENT_DETAILS_SHORT.GET.receive:
                triggerSave();
                actionDispatchers.stateRequest({
                    activeIndex: 0,
                    page: SM_TAB_PATIENT_DETAILS.route,
                    parent: SM_PATIENT_SEARCH.route,
                    data: state
                });
                break;
                
            default:
                break;
        }
    }, [message, results, findPatientBillingGroup, findPatientBillingGroupLoaded, state, triggerSave, actionDispatchers]);
    
    // Event handlers
    const handleCriteriaChange = useCallback((e) => {
        const criteria = e.value;
        let placeholder = '';
        let dob = null;
        
        switch (criteria) {
            case Actions.SEARCH_SURNAME:
                placeholder = 'Surname';
                break;
            case Actions.SEARCH_FIRSTNAME:
                placeholder = 'First Name';
                break;
            case Actions.SEARCH_ALTREF:
                placeholder = 'Alt Reference';
                break;
            case Actions.SEARCH_EMAIL:
                placeholder = 'Email';
                break;
            case Actions.SEARCH_POSTCODE:
                placeholder = 'Postcode';
                break;
            case Actions.SEARCH_TELNUMBER:
                placeholder = 'Telephone Number';
                break;
            case Actions.SEARCH_DOB:
            case Actions.SEARCH_DOB2:
                placeholder = 'Date of Birth';
                dob = new Date();
                break;
            case Actions.SEARCH_PROVIDER:
                placeholder = 'Provider';
                break;
            default:
                break;
        }
        
        setState(prevState => ({
            ...prevState,
            searchCriteria: criteria,
            placeHolder: placeholder,
            dob: dob,
            searchString: ''
        }));
        triggerSave();
    }, [triggerSave]);
    
    const handleInputChange = useCallback((e) => {
        setState(prevState => ({
            ...prevState,
            searchString: e.target.value
        }));
    }, []);
    
    const handleCheckboxChange = useCallback((field, value) => {
        setState(prevState => ({
            ...prevState,
            checkboxes: {
                ...prevState.checkboxes,
                [field]: value
            }
        }));
        triggerSave();
    }, [triggerSave]);
    
    const handleSearch = useCallback(() => {
        const { searchCriteria, searchString, dob, provider, checkboxes } = state;
        
        switch (searchCriteria) {
            case Actions.SEARCH_SURNAME:
                if (searchString.trim()) {
                    actionDispatchers.getPatientsBySurname(`surname/${searchString}/${checkboxes.includeInactives}`);
                }
                break;
            case Actions.SEARCH_FIRSTNAME:
                if (searchString.trim()) {
                    actionDispatchers.getPatientsByFirstname(`firstname/${searchString}/${checkboxes.includeInactives}`);
                }
                break;
            case Actions.SEARCH_ALTREF:
                if (searchString.trim()) {
                    actionDispatchers.getPatientsByAltRef(`altref/${searchString}/${checkboxes.includeInactives}`);
                }
                break;
            case Actions.SEARCH_EMAIL:
                if (searchString.trim()) {
                    actionDispatchers.getPatientsByEmail(`email/${searchString}/${checkboxes.includeInactives}`);
                }
                break;
            case Actions.SEARCH_POSTCODE:
                if (searchString.trim()) {
                    actionDispatchers.getPatientsByPostcode(`postcode/${searchString}/${checkboxes.includeInactives}`);
                }
                break;
            case Actions.SEARCH_TELNUMBER:
                if (searchString.trim()) {
                    actionDispatchers.getPatientsByTelnumber(`telnumber/${searchString}/${checkboxes.includeInactives}`);
                }
                break;
            case Actions.SEARCH_DOB:
            case Actions.SEARCH_DOB2:
                if (dob) {
                    const dobString = moment(dob).format('YYYY-MM-DD');
                    actionDispatchers.getPatientsByDOB(`dob/${dobString}/${checkboxes.includeInactives}`);
                }
                break;
            case Actions.SEARCH_PROVIDER:
                if (provider && provider.id) {
                    actionDispatchers.getPatientsByProvider(`provider/${provider.id}/${checkboxes.includeInactives}`);
                }
                break;
            default:
                actionDispatchers.getAllPatients(checkboxes.includeInactives);
                break;
        }
    }, [state, actionDispatchers]);
    
    const handleAddPatient = useCallback(() => {
        showAddPatientPage(actionDispatchers, loginIdentity, SM_PATIENT_SEARCH.route, state);
    }, [actionDispatchers, loginIdentity, state]);
    
    const handleCloseClick = useCallback(() => {
        actionDispatchers.stateRequest({ activeIndex: 0, page: exitState, data: state });
    }, [actionDispatchers, exitState, state]);
    
    const handleDoubleClick = useCallback((e) => {
        if (e.data) {
            showPatientDetailsPage(actionDispatchers, e.data.id, loginIdentity);
        }
    }, [actionDispatchers, loginIdentity]);
    
    const handleSelectionChange = useCallback((e) => {
        setState(prevState => ({
            ...prevState,
            dataTableSelectValue: e.value
        }));
        
        if (e.value) {
            actionDispatchers.getBillingGroup(e.value.id, null);
        }
        triggerSave();
    }, [actionDispatchers, triggerSave]);
    
    const handlePageChange = useCallback((e) => {
        setState(prevState => ({
            ...prevState,
            first: e.first,
            rows: e.rows
        }));
        triggerSave();
    }, [triggerSave]);
    
    // Template functions
    const genderTemplate = useCallback((rowData) => {
        if (rowData.gender === CONST_MALE) {
            return <img alt="male" src={ICON_PATIENT_MALE} style={{ width: '16px', height: '16px' }} />;
        } else if (rowData.gender === CONST_FEMALE) {
            return <img alt="female" src={ICON_PATIENT_FEMALE} style={{ width: '16px', height: '16px' }} />;
        }
        return null;
    }, []);
    
    const balanceTemplate = useCallback((rowData) => {
        if (rowData.balance) {
            return converter.formatCurrency(rowData.balance);
        }
        return null;
    }, []);
    
    const dobTemplate = useCallback((rowData) => {
        return dateTemplate(rowData.dob);
    }, []);
    
    // Memoized values
    const searchCriteriaOptions = useMemo(() => [
        Actions.SEARCH_SURNAME,
        Actions.SEARCH_FIRSTNAME,
        Actions.SEARCH_ALTREF,
        Actions.SEARCH_EMAIL,
        Actions.SEARCH_POSTCODE,
        Actions.SEARCH_TELNUMBER,
        Actions.SEARCH_DOB,
        Actions.SEARCH_DOB2,
        Actions.SEARCH_PROVIDER,
    ], []);
    
    const menuModel = useMemo(() => [
        {
            label: TB_PATIENT_DETAILS.text,
            icon: TB_PATIENT_DETAILS.icon,
            command: () => handleDoubleClick({ data: state.dataTableSelectValue })
        },
        {
            label: TB_PATIENT_TPLANS.text,
            icon: TB_PATIENT_TPLANS.icon,
            command: () => {
                if (state.dataTableSelectValue) {
                    showTreatmentPlanHistoryPage(actionDispatchers, state.dataTableSelectValue.id, loginIdentity);
                }
            }
        },
        {
            label: TB_PATIENT_PPLANS.text,
            icon: TB_PATIENT_PPLANS.icon,
            command: () => {
                if (state.dataTableSelectValue) {
                    showPaymentPlanHistoryPage(actionDispatchers, state.dataTableSelectValue.id, loginIdentity);
                }
            }
        }
    ], [state.dataTableSelectValue, actionDispatchers, loginIdentity, handleDoubleClick]);
    
    const renderOptionalInput = () => {
        switch (state.searchCriteria) {
            case Actions.SEARCH_DOB:
            case Actions.SEARCH_DOB2:
                return (
                    <Calendar
                        value={state.dob}
                        onChange={(e) => setState(prev => ({ ...prev, dob: e.value }))}
                        dateFormat="dd/mm/yy"
                        showButtonBar
                        style={{ width: '200px' }}
                    />
                );
            case Actions.SEARCH_PROVIDER:
                return (
                    <Dropdown
                        value={state.provider}
                        options={usersShort || []}
                        onChange={(e) => setState(prev => ({ ...prev, provider: e.value }))}
                        optionLabel="fullName"
                        placeholder="Select Provider"
                        style={{ width: '200px' }}
                    />
                );
            default:
                return (
                    <InputText
                        ref={searchInput}
                        value={state.searchString}
                        onChange={handleInputChange}
                        placeholder={state.placeHolder}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                        style={{ width: '200px' }}
                    />
                );
        }
    };
    
    const headerTemplate = useMemo(() => (
        <div className="p-grid p-align-center" style={{ padding: '10px' }}>
            <div className="p-col-4">
                <h3>Patient Search</h3>
                <Button
                    label={TB_ADD_PATIENT.text}
                    icon={TB_ADD_PATIENT.icon}
                    onClick={handleAddPatient}
                    className="p-button-sm p-button-success"
                    style={{ marginTop: '5px' }}
                />
            </div>
            <div className="p-col-4 p-text-center">
                <div style={{ fontSize: '14px', color: '#666' }}>
                    {state.results ? `${state.results.length} patients found` : 'No results'}
                </div>
            </div>
            <div className="p-col-4 p-text-right">
                <div className="p-grid p-align-center p-justify-end">
                    <div className="p-col-auto">
                        <Dropdown
                            value={state.searchCriteria}
                            options={searchCriteriaOptions}
                            onChange={handleCriteriaChange}
                            style={{ width: '150px' }}
                        />
                    </div>
                    <div className="p-col-auto">
                        {renderOptionalInput()}
                    </div>
                    <div className="p-col-auto">
                        <Button
                            icon="pi pi-search"
                            onClick={handleSearch}
                            className="p-button-sm"
                            style={{ marginLeft: '5px' }}
                        />
                    </div>
                </div>
                <div style={{ marginTop: '10px' }}>
                    {checkBox(
                        'includeInactives',
                        state.checkboxes.includeInactives,
                        'Include Inactive Patients',
                        (field, value) => handleCheckboxChange(field, value)
                    )}
                </div>
            </div>
        </div>
    ), [state, searchCriteriaOptions, handleCriteriaChange, handleSearch, handleAddPatient, handleCheckboxChange, renderOptionalInput]);
    
    // Show loading state if data not ready
    if (!usersLoaded || !templateNotesLoaded) {
        return <ProgressBar mode="indeterminate" style={{ height: '6px' }} />;
    }
    
    // Main render
    return (
        <div id="detailPanel">
            <Toast ref={tabletGrowl} />
            
            <Toolbar
                right={
                    <React.Fragment>
                        {tb_boilerPlateRight(
                            () => window.open(HELP_FIND_PATIENT),
                            ICON_HELP,
                            TT_KnowledgeBase.label,
                            1,
                            true
                        )}
                        {tb_boilerPlateRight(
                            handleCloseClick,
                            TB_SAVE_AND_EXIT.icon,
                            TB_SAVE_AND_EXIT.text,
                            2
                        )}
                    </React.Fragment>
                }
            />
            
            {/* Main search panel with proper header */}
            <Panel 
                header={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span>Patient Search</span>
                        <span style={{ fontSize: '14px', fontWeight: 'normal' }}>Matching Patients ({state.results ? state.results.length : 0})</span>
                    </div>
                }
                style={{ margin: '5px 0px 0px 0px' }}
            >
                {/* Search controls section */}
                <div className="p-grid p-align-center" style={{ marginBottom: '10px' }}>
                    <div className="p-col-2">
                        <Button
                            label={TB_ADD_PATIENT.text}
                            icon={TB_ADD_PATIENT.icon}
                            onClick={handleAddPatient}
                            className="p-button-sm p-button-success"
                        />
                    </div>
                    <div className="p-col-6 p-text-center">
                        <div style={{ fontSize: '14px', color: '#666' }}>
                            {state.results ? `${state.results.length} patients found` : '0 patients found'}
                        </div>
                    </div>
                    <div className="p-col-4 p-text-right">
                        <div className="p-grid p-align-center p-justify-end">
                            <div className="p-col-auto">
                                <Dropdown
                                    value={state.searchCriteria}
                                    options={searchCriteriaOptions}
                                    onChange={handleCriteriaChange}
                                    style={{ width: '150px' }}
                                />
                            </div>
                            <div className="p-col-auto">
                                {renderOptionalInput()}
                            </div>
                            <div className="p-col-auto">
                                <Button
                                    icon="pi pi-search"
                                    onClick={handleSearch}
                                    className="p-button-sm"
                                    style={{ marginLeft: '5px' }}
                                />
                            </div>
                            <div className="p-col-auto">
                                {checkBox(
                                    'includeInactives',
                                    state.checkboxes.includeInactives,
                                    'Include Inactives',
                                    (field, value) => handleCheckboxChange(field, value)
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Context menu and data table */}
                <ContextMenu model={menuModel} ref={(el) => el && (el.show = (e) => el.show(e))} />
                
                <DataTable
                    value={state.results || []}
                    paginator
                    rows={state.rows}
                    first={state.first}
                    onPage={handlePageChange}
                    selection={state.dataTableSelectValue}
                    onSelectionChange={handleSelectionChange}
                    selectionMode="single"
                    onRowDoubleClick={handleDoubleClick}
                    rowClassName={rowBGColour}
                    emptyMessage="No patients found"
                >
                    <Column
                        body={genderTemplate}
                        style={{ width: '40px', textAlign: 'center' }}
                        header=""
                    />
                    <Column
                        body={(rowData) => nhsTemplate(rowData.nhsNumber)}
                        style={{ width: '40px', textAlign: 'center' }}
                        header="NHS"
                    />
                    <Column
                        field="principlePayee"
                        body={(rowData) => rowData.principlePayee ? 'PP' : ''}
                        style={{ width: '40px', textAlign: 'center' }}
                        header="GP"
                    />
                    <Column
                        field="principleGroupDescription"
                        header="Group"
                        style={{ width: '120px' }}
                    />
                    <Column
                        body={balanceTemplate}
                        style={{ width: '80px', textAlign: 'right' }}
                        header="Balance"
                    />
                    <Column
                        field="titleAbbrev"
                        header="Title"
                        style={{ width: '60px' }}
                    />
                    <Column
                        field="firstName"
                        header="First Name"
                        style={{ width: '120px' }}
                    />
                    <Column
                        field="lastName"
                        header="Last Name"
                        style={{ width: '120px' }}
                    />
                    <Column
                        body={dobTemplate}
                        header="DOB"
                        style={{ width: '100px' }}
                    />
                </DataTable>
            </Panel>
            
            {/* Family Group section - Always visible as per reference screenshot */}
            <Panel header={HM_PatientBillingGroup.header} style={{ margin: '15px 0px 0px 0px' }}>
                <div className="p-grid p-fluid">
                    <BillingGroup
                        billingGroup={state.findPatientBillingGroup || findPatientBillingGroup}
                        firstMember={state.firstMember}
                        memberRows={state.memberRows}
                        onPageFlex={(e) => {
                            setState(prev => ({
                                ...prev,
                                firstMember: e.first,
                                memberRows: e.rows
                            }));
                            triggerSave();
                        }}
                        onSelectionChange={(e) => {
                            setState(prev => ({ ...prev, memberSelection: e.value }));
                            triggerSave();
                        }}
                        memberSelection={state.memberSelection}
                        toolbarCallbacks={{
                            [TB_ADD_PATIENT.id]: handleAddPatient,
                            [TB_PATIENT_DETAILS.id]: () => {
                                if (state.dataTableSelectValue) {
                                    showPatientDetailsPage(actionDispatchers, state.dataTableSelectValue.id, loginIdentity);
                                }
                            },
                            [TB_PATIENT_TPLANS.id]: () => {
                                if (state.dataTableSelectValue) {
                                    showTreatmentPlanHistoryPage(actionDispatchers, state.dataTableSelectValue.id, loginIdentity);
                                }
                            },
                            [TB_PATIENT_PPLANS.id]: () => {
                                if (state.dataTableSelectValue) {
                                    showPaymentPlanHistoryPage(actionDispatchers, state.dataTableSelectValue.id, loginIdentity);
                                }
                            },
                            [HM_SEND_SMS.id]: () => {
                                // SMS functionality - to be implemented
                            },
                            [HM_SEND_EMAIL.id]: () => {
                                // Email functionality - to be implemented
                            },
                            [TB_SAVE_AND_EXIT.id]: handleCloseClick,
                        }}
                        onShowDetails={(id) => showPatientDetailsPage(actionDispatchers, id, loginIdentity)}
                        onChange={() => {}}
                        onRemoveFromBillingGroup={() => {
                            // Remove from billing group - to be implemented
                        }}
                        fromFind={true}
                    />
                </div>
            </Panel>
        </div>
    );
};

// Pure hooks-based component - no connect HOC needed

// Export both versions for gradual migration
export const ConnectedFindPatients = FindPatients;
export default FindPatients;